import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, Tooltip, IconButton, Fab } from '@material-ui/core';
import { Add, CloudDownload } from '@material-ui/icons';
import { BackButton, localization, getDimensionsOfPagination } from '../../sc-utilities';
import { Delete, Refresh } from '@material-ui/icons';
import MaterialTable from 'material-table';
import { hasPermission } from '../../sc-utilities';
import TrialVoucherDelete from './TrialVoucherDelete';
import * as actions from './store/trialVoucherActions';
import * as action from '../../np-core/Auth/store/authActions';
import Toolbar from '../../sc-utilities/Toolbar';
import moment from 'moment';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const TrialVoucherList = props => {
    const { classes, translations, permissions, onFetchTrialVouchers, onFetchTrialVouchersCount, fetching, deleting, saveColumnsFilter, activeCustomerID, onBulkDeleteTrialVoucher, tableColumns, redirectHandler, handleBulkDownload, tableRef } = props;
    const [deleteModalState, setDeleteModalState] = useState({ open: false, trialVoucherIDs: [] });
    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);
    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: true,
        search: false,
        minBodyHeight: window.innerHeight - 205 < 300 ? 300 : window.innerHeight - 205,
        maxBodyHeight: window.innerHeight - 205,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? '#f3f3f3' : '#ffffff' };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    }

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);
        q['ExpiryDate'] = q['ExpiryDate'] ? moment(q['ExpiryDate']).set({ hour: 0, minute: 0, second: 0 }).format('YYYY-MM-DD HH:mm:ss') : null;
        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;

        let count = await onFetchTrialVouchersCount(query);
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count)
            query['pageSize'] = dimensions?.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count ? await onFetchTrialVouchers(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography >{translations['trial_vouchers'] || 'Trial Vouchers'}</Typography>
                {hasPermission(permissions, 'trial_voucher_create') ? <Fab
                    id="create_trial_voucher"
                    variant="extended"
                    size="small"
                    color="primary"
                    aria-label="Add"
                    style={{ borderRadius: '1px' }}
                    className={classes.fab}
                    onClick={() => redirectHandler(`/billing/trial-vouchers/create`)}
                >
                    <Add size="small" />
                    {translations["create_new"] || "Create new"}
                </Fab> : null}
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    }

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, trialVoucherIDs: [] });
    }

    const handleCloseDanger = async () => {
        await onBulkDeleteTrialVoucher({ TrialVoucherIDs: deleteModalState.trialVoucherIDs, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, trialVoucherIDs: [] });
        tableRef.current && tableRef.current.onQueryChange()
    };

    const handleBulkDelete = trialVoucherIDs => {
        setDeleteModalState({ ...deleteModalState, open: true, trialVoucherIDs: trialVoucherIDs });
    };

    // Table Settings
    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["trial_vouchers"] || "Trial Vouchers"}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(trialVoucher => trialVoucher.TrialVoucherID.toString()))
        },
        {
            tooltip: `${translations["bulk_download_all_selected"] || "Bulk download all selected"} ${translations["trial_vouchers"] || "Trial Vouchers"}`,
            icon: <CloudDownload />,
            onClick: (rowData) => handleBulkDownload(rowData.map(trialVoucher => trialVoucher.TrialVoucherID))
        }
    ]

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`TrialVoucher`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID })
        return newColumnState
    }

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                localization={localization(translations)}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {hasPermission(permissions, 'trial_voucher_delete') ? (
                <TrialVoucherDelete
                    activeCustomerID={activeCustomerID}
                    entityName={translations['trial_vouchers'] || 'Trial Vouchers'}
                    translations={translations}
                    handleCloseDanger={handleCloseDanger}
                    handleCloseSafe={handleCloseSafe}
                    deleteModalState={deleteModalState}
                    deleting={deleting}
                />
            ) : null}
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
        fetching: state.trialVoucher.trialVouchers.fetching,
        deleting: state.trialVoucher.trialVouchers.deleting
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchTrialVouchersCount: filterParams => dispatch(actions.fetchTrialVouchersCount(filterParams)),
        onFetchTrialVouchers: filterParams => dispatch(actions.fetchTrialVouchers(filterParams)),
        onBulkDeleteTrialVoucher: params => dispatch((actions.bulkDeleteTrialVoucher(params))),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(TrialVoucherList));